import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import notificationService from '../services/notificationService';
import { MenuIcon, CloseIcon, LogoutIcon, BellIcon } from './Icons';
import './Layout.css';

const NOTIFICATION_PAGE_SIZE = 10;

const appendUniqueNotifications = (existing, incoming) => {
  const seen = new Set(existing.map(notification => notification._id));
  return [
    ...existing,
    ...incoming.filter(notification => {
      if (seen.has(notification._id)) return false;
      seen.add(notification._id);
      return true;
    })
  ];
};

function Header({ isSidebarOpen, toggleSidebar, hasWorkspaceTabs }) {
  const { admin, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const notificationRef = useRef(null);
  const notificationFetchRef = useRef(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsLoadingMore, setNotificationsLoadingMore] = useState(false);
  const [notificationPage, setNotificationPage] = useState(1);
  const [notificationHasMore, setNotificationHasMore] = useState(false);

  const getPageMeta = (path) => {
    if (path === '/dashboard' || path === '/') return { section: 'Workspace', title: 'Dashboard Overview' };

    if (path === '/merchants') return { section: 'Operations', title: 'Merchants Management' };
    if (path === '/merchant-search') return { section: 'Operations', title: 'Merchant Search' };
    if (path === '/merchants/new') return { section: 'Operations', title: 'Add New Merchant' };
    if (path.startsWith('/merchants/')) return { section: 'Operations', title: path.endsWith('/view') ? 'Merchant View' : 'Edit Merchant' };

    if (path === '/transactions') return { section: 'Operations', title: 'Transactions' };
    if (path === '/finance-reconciliation') return { section: 'Operations', title: 'Finance Reconciliation' };
    if (path.startsWith('/finance-reconciliation/')) return { section: 'Operations', title: 'Finance Report Detail' };

    if (path === '/users') return { section: 'Operations', title: 'Users Management' };
    if (path === '/users/new') return { section: 'Operations', title: 'Add New User' };
    if (path.startsWith('/users/')) return { section: 'Operations', title: 'Edit User' };

    if (path === '/logs') return { section: 'System', title: 'System Audit Logs' };
    if (path === '/change-history') return { section: 'System', title: 'Change History' };

    if (path === '/games/currencies') return { section: 'Game Management', title: 'Currencies' };
    if (path === '/games/categories') return { section: 'Game Management', title: 'Categories' };
    if (path === '/games/sub-categories') return { section: 'Game Management', title: 'Sub-Categories' };
    if (path === '/games/countries') return { section: 'Game Management', title: 'Countries' };
    if (path === '/games/sources') return { section: 'Game Management', title: 'Provider Sources' };
    if (path === '/games/providers') return { section: 'Game Management', title: 'Providers' };
    if (path === '/payment-gateways/categories') return { section: 'Payment Gateway Management', title: 'Categories' };
    if (path === '/payment-gateways/sources') return { section: 'Payment Gateway Management', title: 'Provider Sources' };
    if (path === '/payment-gateways/providers') return { section: 'Payment Gateway Management', title: 'Providers' };
    if (path === '/payment-gateways/master-providers') return { section: 'L1 Master', title: 'Payment Gateway Providers' };
    if (path === '/games/master-providers') return { section: 'L1 Master', title: 'Providers' };
    if (path.startsWith('/games/master-providers/')) return { section: 'L1 Master', title: 'Provider View' };

    if (path === '/tickets') return { section: 'Operations', title: 'Ticketing' };
    if (path === '/tickets/workflow') return { section: 'Operations', title: 'Ticket Workflow' };
    if (path.startsWith('/tickets/')) return { section: 'Operations', title: 'Ticket Detail' };

    if (path === '/settings') return { section: 'System', title: 'Settings' };
    if (path === '/profile') return { section: 'Account', title: 'My Profile' };

    return { section: 'Backoffice', title: 'Workspace' };
  };

  const fetchNotifications = useCallback(async ({ page = 1, append = false } = {}) => {
    if (!admin?._id) return;
    if (notificationFetchRef.current) return;
    notificationFetchRef.current = true;

    if (append) {
      setNotificationsLoadingMore(true);
    } else {
      setNotificationsLoading(true);
    }

    const res = await notificationService.getNotifications({ page, limit: NOTIFICATION_PAGE_SIZE });
    if (res.success) {
      const nextNotifications = res.data || [];
      setNotifications(prev => (
        append ? appendUniqueNotifications(prev, nextNotifications) : nextNotifications
      ));
      setUnreadCount(res.unread_count || 0);
      const currentPage = res.pagination?.page || page;
      setNotificationPage(currentPage);
      setNotificationHasMore(currentPage < (res.pagination?.pages || 1));
    }
    if (append) {
      setNotificationsLoadingMore(false);
    } else {
      setNotificationsLoading(false);
    }
    notificationFetchRef.current = false;
  }, [admin?._id]);

  const fetchUnreadCount = useCallback(async () => {
    if (!admin?._id) return;
    const res = await notificationService.getUnreadCount();
    if (res.success) {
      setUnreadCount(res.data?.unread_count || 0);
    }
  }, [admin?._id]);

  useEffect(() => {
    if (!admin?._id) {
      setNotifications([]);
      setUnreadCount(0);
      setNotificationPage(1);
      setNotificationHasMore(false);
      return undefined;
    }

    fetchNotifications({ page: 1 });
    const interval = setInterval(fetchUnreadCount, 60000);
    window.addEventListener('focus', fetchUnreadCount);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', fetchUnreadCount);
    };
  }, [admin?._id, fetchNotifications, fetchUnreadCount]);

  useEffect(() => {
    if (!isNotificationsOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setIsNotificationsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isNotificationsOpen]);

  const toggleNotifications = () => {
    setIsNotificationsOpen(prev => {
      const nextOpen = !prev;
      if (nextOpen) fetchNotifications({ page: 1 });
      return nextOpen;
    });
  };

  const loadMoreNotifications = useCallback(async () => {
    if (!notificationHasMore || notificationsLoading || notificationsLoadingMore) return;
    await fetchNotifications({ page: notificationPage + 1, append: true });
  }, [
    fetchNotifications,
    notificationHasMore,
    notificationPage,
    notificationsLoading,
    notificationsLoadingMore
  ]);

  const handleNotificationScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    if (distanceFromBottom <= 48) {
      loadMoreNotifications();
    }
  };

  const formatNotificationTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString();
  };

  const markNotificationReadLocally = (id, nextUnreadCount = null) => {
    setNotifications(prev => prev.map(notification => (
      notification._id === id
        ? { ...notification, read_at: notification.read_at || new Date().toISOString() }
        : notification
    )));
    setUnreadCount(prev => (
      typeof nextUnreadCount === 'number' ? nextUnreadCount : Math.max(0, prev - 1)
    ));
  };

  const openNotificationTarget = async (notification) => {
    if (!notification) return;
    if (!notification.read_at) {
      const res = await notificationService.markAsRead(notification._id);
      if (res.success) markNotificationReadLocally(notification._id, res.unread_count);
    }
    setIsNotificationsOpen(false);
    navigate(notification.target_url || '/tickets');
  };

  const markAllNotificationsRead = async () => {
    if (unreadCount === 0) return;
    const res = await notificationService.markAllAsRead();
    if (res.success) {
      setNotifications(prev => prev.map(notification => ({
        ...notification,
        read_at: notification.read_at || new Date().toISOString()
      })));
      setUnreadCount(0);
    }
  };

  const pageMeta = getPageMeta(location.pathname);
  const roleLabel = admin?.role?.replace(/_/g, ' ') || 'Administrator';
  const userInitial = admin?.username?.charAt(0).toUpperCase() || 'A';

  return (
    <header className={`header ${!isSidebarOpen ? 'full-width' : ''} ${hasWorkspaceTabs ? 'with-workspace-tabs' : ''}`}>
      <div className="header-left">
        <button
          className="toggle-btn"
          onClick={toggleSidebar}
          aria-controls="app-sidebar"
          aria-expanded={isSidebarOpen}
          aria-label="Toggle Sidebar"
        >
          {isSidebarOpen ? <CloseIcon /> : <MenuIcon />}
        </button>
        <div className="header-title-block">
          <span className="header-section">{pageMeta.section}</span>
          <span className="header-title">{pageMeta.title}</span>
        </div>
      </div>

      <div className="header-right">
        <div className="notification-wrapper" ref={notificationRef}>
          <button
            type="button"
            className={`notification-trigger ${isNotificationsOpen ? 'active' : ''}`}
            onClick={toggleNotifications}
            aria-label="Notifications"
            title="Notifications"
          >
            <BellIcon size={18} />
            {unreadCount > 0 && (
              <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
            )}
          </button>

          {isNotificationsOpen && (
            <div className="notification-menu">
              <div className="notification-menu-header">
                <div>
                  <div className="notification-menu-title">Notifications</div>
                  <div className="notification-menu-subtitle">
                    {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
                  </div>
                </div>
                {unreadCount > 0 && (
                  <button type="button" className="notification-mark-all" onClick={markAllNotificationsRead}>
                    Mark read
                  </button>
                )}
              </div>

              <div className="notification-list" onScroll={handleNotificationScroll}>
                {notificationsLoading && notifications.length === 0 ? (
                  <div className="notification-empty">Loading...</div>
                ) : notifications.length === 0 ? (
                  <div className="notification-empty">No notifications yet.</div>
                ) : (
                  <>
                    {notifications.map(notification => (
                      <button
                        key={notification._id}
                        type="button"
                        className={`notification-item ${notification.read_at ? '' : 'unread'}`}
                        onClick={() => openNotificationTarget(notification)}
                      >
                        <span className="notification-dot" />
                        <span className="notification-content">
                          <span className="notification-title">{notification.title}</span>
                          <span className="notification-message">{notification.message}</span>
                          <span className="notification-time">{formatNotificationTime(notification.created_at)}</span>
                        </span>
                      </button>
                    ))}
                    {notificationsLoadingMore && (
                      <div className="notification-loading-more">Loading more...</div>
                    )}
                    {!notificationHasMore && notifications.length > NOTIFICATION_PAGE_SIZE && (
                      <div className="notification-loading-more">End of notifications</div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="user-profile">
          <Link to="/profile" className="user-avatar" title="View Profile" style={{ textDecoration: 'none' }}>
            {userInitial}
          </Link>
          <div className="user-info">
            <Link to="/profile" className="user-name" style={{ textDecoration: 'none', color: 'inherit' }}>{admin?.username || 'Admin'}</Link>
            <span className="user-role">{roleLabel}</span>
          </div>
          <div className="user-divider"></div>
          <button
            type="button"
            onClick={logout}
            className="logout-icon-btn"
            aria-label="Logout"
            title="Logout"
          >
            <LogoutIcon />
          </button>
        </div>
      </div>
    </header>
  );
}

export default Header;
